<template>
<div class="chapter">
<div class="inner_page">
<div class="element element-bodymatter element-container-single element-type-chapter element-with-heading" epub:type="chapter" id="chapter-1" role="doc-chapter">
<div class="heading heading-container-single heading-size-full heading-format-full heading-alignment-flexible heading-without-image">
<div class="heading-contents">
<div class="title-subtitle-block title-block-title-is-element-number">
<div class="element-number-block">
</div>
<div class="title-block">
<h1 class="element-title case-mixed"><span class="element-number-term">Chapter</span> <span class="element-number-number">One</span></h1>
</div>
</div>
</div>
</div>
<div class="text" id="chapter-1-text">
<p class="first first-in-chapter first-full-width first-with-first-letter-s"><span class="drop-cap">S</span>he stands on the doorstep. She’s shivering. She isn’t wearing a coat and I can see the outline of her breasts through her thin blouse. She sees me looking and pulls her cardigan around her.</p>
<p class="subsq">‘Hello,’ she says. ‘It’s freezing isn’t it?’</p>
<p class="subsq">Her lips quiver with the cold. She looks shy and apologetic. Some hair has escaped her loose ponytail and she brushes it back. She looks at me through rain-splattered glasses.</p>
<p class="subsq">‘I’m Sharni,’ she says. ‘We’ve just moved in to number 24, next door.’</p>
<p class="subsq">She points and my eyes follow her direction to a removal van. The house had been empty for a few months and Chris and I had wondered who might move in.</p>
<p class="subsq">‘I’m Clare,’ I say. ‘It’s nice to meet you.’</p>
<p class="subsq">She seems nervous and embarrassed.</p>
<p class="subsq">‘I’m sorry to be a pain, already,’ she smiles. ‘But I’ve been sent some flowers and I can’t seem to find a vase anywhere. There are just so many boxes and …’</p>
<p class="subsq">‘Oh, of course, come in,’ I say opening the door wider.</p>
<p class="subsq">She closes it quickly behind her but not before a flurry of autumn leaves has blown in with her. Their rustic brown matches the colour of her hair.</p>
<p class="subsq">‘It’s lovely and warm in here,’ she comments.</p>
<p class="subsq">‘Do you not have heating?’</p>
<p class="subsq">‘Oh yes, but with the door open all the time it’s impossible to stay warm.’</p>
<p class="subsq">She looks vaguely familiar and I try to recall where I have seen her before.</p>
<p class="subsq">‘I’ll get you a vase,’ I say, leading her into the lounge. ‘Would you like a coffee?’</p>
<p class="subsq">She turns away and rubs her glasses on the sleeve of her cardigan.</p>
<p class="subsq">‘I’m steamed up,’ she laughs. ‘A coffee would be great.’</p>
<p class="subsq">Small gold hoops dangle from her earlobes and I notice ink smudges on her hands. Newspaper print I imagine. I remember Chris and I were covered in it when we moved in<i>.</i></p>
<p class="subsq"><i>‘</i>If we ever move again, remind me not to use newspaper,’ he’d complained.</p>
<p class="subsq">I search under the sink for a vase. My hand encounters dirty dusters and old bottles of cleaning liquid, but no vase. A mousetrap slams shut on my finger and I fight back a cry. I hear Sharni talking to Ben. Her voice is warm and soft. Ben gurgles happily. I try another cupboard without success and finally reach for the hand-painted vase on the top shelf of the Welsh dresser. Chris had bought it for me on our honeymoon. I hesitate for a second. It’s not like she’ll have it for long. She’ll probably give it back as soon as she’s unpacked.</p>
<p class="subsq">The kettle clicks and I make the coffee.</p>
<p class="subsq">‘Oh, that’s lovely,’ she says on seeing the vase. She’s on the floor helping Ben colour in his rabbit pictures.</p>
<p class="subsq">‘My husband bought it for me when we were honeymooning in Ireland.’</p>
<p class="subsq">‘If you’re sure?’ she asks. ‘I’ll bring it back tomorrow. It’s just I don’t want the flowers to die.’</p>
<p class="subsq">‘That’s fine,’ I say, pushing my lesson plans off the coffee table and placing a mug in front of her.</p>
<p class="subsq">‘He’s adorable,’ she says, looking at Ben.</p>
<p class="subsq">‘He is,’ I agree.</p>
<p class="subsq">She strokes the top of his head, her hand lingering.</p>
<p class="subsq">‘You have a lovely home,’ she says, glancing around.</p>
<p class="subsq">‘Thank you.’</p>
<p class="subsq">‘I’m keen to get ideas. I want our house to look really nice. You’ve done a great job here.’</p>
<p class="subsq">She fingers the art décor lamp on the side table and I smile. Ben begins to get irritable and I bend down to him.</p>
<p class="subsq">‘Do you mind if I ask where you bought this?’</p>
<p class="subsq">‘John Lewis, if I remember. They have lovely things there.’</p>
<p class="subsq">Ben lets out a burp and we both laugh.</p>
<p class="subsq">‘How old is he?’ she asks.</p>
<p class="subsq">‘Almost two,’ I say.</p>
<p class="subsq">He struggles from my arms and wobbles towards Sharni like a newborn fawn.</p>
<p class="subsq">‘He’s just discovered his legs,’ I laugh.</p>
<p class="subsq">She catches him as he tumbles towards her.</p>
<p class="subsq">‘Do you have children?’ I ask.</p>
<p class="subsq">Her face clouds over. It occurs to me that maybe I shouldn’t have asked. She then smiles and I wonder if I imagined it.</p>
<p class="subsq">‘No,’ she says flatly.</p>
<p class="subsq">I struggle to think of something to say.</p>
<p class="subsq">‘Are you in interior design?’ she asks.</p>
<p class="subsq">I laugh.</p>
<p class="subsq">‘Me? No, I wouldn’t know where to start. I’m a teacher, well, only part-time now that we have Ben.’</p>
<p class="subsq">‘But this room is gorgeous, you have excellent taste. I’ll have to pick your brains when I start decorating ours.’</p>
<p class="subsq">‘It’ll be nice to have neighbours of our own age,’ I say.</p>
<p class="subsq">Her eyes shine. She places her mug on the table and leans towards me.</p>
<p class="subsq">‘I’ve got a great idea, why don’t you and your husband come over Saturday evening for house-warming drinks. We can get to know each other better.’</p>
<p class="subsq">‘But you’ll be up to your eyes won’t you?’ I say, surprised at the invite.</p>
<p class="subsq">‘We’ll need a break. Do say you’ll come.’</p>
<p class="subsq">‘I’ll need to check with Chris, my husband, but I’m sure it will be okay.’</p>
<p class="subsq">‘Great,’ she smiles, getting up from the couch. ‘Shall we say about eight? If you can’t get a sitter then bring the toddler with you.’</p>
<p class="subsq">I open my mouth to speak and then change my mind. I had hoped we could take Ben with us. I hate leaving him with sitters but I imagine she wants an adult evening.</p>
<p class="subsq">‘Thanks,’ I say hugging Ben close. ‘Enjoy your flowers.’</p>
<p class="subsq">‘Flowers?’ she questions.</p>
<p class="subsq">‘The ones you needed the vase for.’</p>
<p class="subsq">‘Oh yes. My mum sent them. Thanks Clare. See you on Saturday.’</p>
<div class="ornamental-break">
<div class="ornamental-break-block ornamental-break-aspect-wide">
<div class="ornamental-break-size-container ornamental-break-width-563">
<img alt="" class="ornamental-break-image" role="presentation" src="images/break-rule-gradient-screen.png"/>
</div>
</div>
</div>
<p class="first first-in-section first-full-width">‘I see someone’s moving in,’ says Helen, nodding towards the removal van.</p>
<p class="subsq">I follow her gaze.</p>
<p class="subsq">‘Yes. I met her this morning. She seems nice.’</p>
<p class="subsq">We watch as a white sofa is carried into the house. I shiver as the cold air cuts through me.</p>
<p class="subsq">‘Do you want a coffee?’ I ask, stepping back into the warm.</p>
<p class="subsq">‘No, I just popped around for the lesson plans. I’ve got a pile of marking to get through.’</p>
<p class="subsq">She closes the door and waits for me to get the plans.</p>
<p class="subsq">‘They’ve invited us for drinks on Saturday,’ I say. I don’t know why I’m telling Helen. Maybe I’m hoping she’ll make me feel better about not wanting to go.</p>
<p class="subsq">‘God, they’d have barely settled in.’</p>
<p class="subsq">‘I know. I’m thinking we should maybe say no.’</p>
<p class="subsq">‘That’s daft. Of course you should go. I’m sure you can get a sitter,’ smiles Helen. ‘You’re always saying how Chris moans because you don’t go out much.’</p>
<p class="subsq">I force a smile. It’s very short notice to ask Kathryn to babysit and I really don’t trust anyone else. It’s all right for Helen. Not having children she couldn’t possibly understand. I know she thinks I’m overanxious and maybe she’s right. It’s not that I wouldn’t like to go out for the evening but it’s not much fun if I’m worrying about Ben all the time.</p>
<p class="subsq">‘I’ll speak to Chris,’ I say.</p>
<p class="subsq">‘How’s Ben?’ she asks, peeking around the door to wave at him.</p>
<p class="subsq">‘He’s great. I love the days I’m home.’</p>
<p class="subsq">She hugs me and opens the front door.</p>
<p class="subsq">‘We need to do a trip to Ikea,’ she says, her face lighting up.</p>
<p class="subsq">‘Great.’</p>
<p class="subsq">‘We’ll get a date at school tomorrow. Better fly.’</p>
<p class="subsq">I watch as a black Audi pulls up outside the house next door. A smartly dressed man emerges from it. He pulls a suitcase from the boot and wheels it to the house. I brush some leaves from the step and close the front door.</p>
</div>
</div>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// chapter-001.vue?71b0815c